'use client';

import { Collapsible } from '../collapsible';
import { Button } from '../button';
import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Text } from '../text';

export const ACTIONS_POSITION = {
  NextToTitle: 'start',
  Opposite: 'end',
} as const;
export type ActionsPosition =
  (typeof ACTIONS_POSITION)[keyof typeof ACTIONS_POSITION];
export type EntityDrawerSectionProps = {
  title: string;
  children: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  actions?: ReactNode;
  /** Where to render actions relative to the title. Default NextToTitle. */
  actionsPosition?: ActionsPosition;
};
export const EntityDrawerSection = ({
  title,
  children,
  collapsible,
  defaultOpen,
  actions,
  actionsPosition = ACTIONS_POSITION.NextToTitle,
}: EntityDrawerSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const isNextToTitle = actionsPosition === ACTIONS_POSITION.NextToTitle;

  if (!collapsible) {
    return (
      <VStack gap="2" alignItems="stretch" w="full">
        <HStack
          justify={isNextToTitle ? 'flex-start' : 'space-between'}
          alignItems="center"
          w="full"
          gap="2"
          flexWrap="wrap"
        >
          <HStack gap="2" alignItems="center" flexWrap="wrap">
            <Text fontSize="sm" fontWeight="semibold">
              {title}
            </Text>
            {isNextToTitle && actions}
          </HStack>
          {!isNextToTitle && actions}
        </HStack>
        <VStack gap="2" alignItems="stretch" w="full">
          {children}
        </VStack>
      </VStack>
    );
  }

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={(details) => setIsOpen(details.open)}
    >
      <VStack gap="4" alignItems="stretch" w="full">
        <Collapsible.Trigger display="flex" alignItems="center">
          <Button variant="ghost" p={0}>
            <HStack gap={2}>
              {isOpen ? (
                <ArrowDown01Icon size={16} />
              ) : (
                <ArrowRight01Icon size={16} />
              )}
            </HStack>
          </Button>
          <Text fontSize="medium" color="fg.muted" cursor="pointer">
            {title}
          </Text>
        </Collapsible.Trigger>
        <Collapsible.Content w="full">
          <Box pl="9">
            <VStack gap="4" alignItems="stretch" w="full">
              {children}
            </VStack>
          </Box>
        </Collapsible.Content>
      </VStack>
    </Collapsible.Root>
  );
};
